import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { LuClock } from "react-icons/lu";
import Seo from "../components/Seo";
import DownloadButton from "../components/DownloadButton";
import { useJackpot, useMarkets, useStarline } from "../hooks/useData";
import { BRAND, siteUrl } from "../utils/config";
import { titleCase } from "../utils/data";

const initials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2);

const Charts = () => {
  const { markets, loading } = useMarkets();
  const starline = useStarline();
  const jackpot = useJackpot();
  const origin = siteUrl();

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "All Charts", item: `${origin}/charts` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "All matka charts",
        itemListElement: markets.map((m, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `${titleCase(m.name)} Jodi & Pana Chart`,
          url: `${origin}/jodi/${m.slug}`,
        })),
      },
    ],
    [markets, origin]
  );

  return (
    <main className="rt-main">
      <Seo
        title={`All Satta Matka Charts – Jodi & Panel Chart | ${BRAND.name}`}
        description={`Open the Jodi chart and Pana (panel) chart of every matka market — Kalyan, Milan, Madhur, Sridevi, Main Bazar, Rajdhani, Time Bazar, Supreme and Ratan Khatri — updated live on ${BRAND.name}.`}
        path="/charts"
        schema={schema}
      />

      <section className="rt-phead">
        <div className="rt-wrap">
          <nav className="rt-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <b>All Charts</b>
          </nav>
          <span className="rt-eyebrow rt-eyebrow--dark">Charts</span>
          <h1>All Matka Charts</h1>
          <p>
            Pick a market to open its week-by-week Jodi chart or Pana (panel) chart. {markets.length || ""} markets,
            updated as soon as results are declared.
          </p>
        </div>
      </section>

      <section className="rt-wrap rt-section">
        <div className="rt-grid">
          {markets.map((m) => (
            <article key={m.slug} className="rt-mkt">
              <span className="rt-mkt__av" aria-hidden="true">
                {initials(m.name)}
              </span>
              <div className="rt-mkt__body">
                <span className={`rt-badge ${m.isOpen ? "rt-badge--open" : "rt-badge--closed"}`}>
                  {m.isOpen ? "Live" : "Closed"}
                </span>
                <h2>{m.name}</h2>
                <p>
                  <LuClock size={13} aria-hidden="true" /> {m.openTime} - {m.closeTime}
                </p>
              </div>
              <div className="rt-mkt__btns">
                <Link to={`/jodi/${m.slug}`} title={`${titleCase(m.name)} Jodi chart`}>
                  Jodi
                </Link>
                <Link to={`/pana/${m.slug}`} title={`${titleCase(m.name)} Pana chart`}>
                  Pana
                </Link>
              </div>
            </article>
          ))}
        </div>
        {!loading && markets.length === 0 && <p className="rt-empty">Markets are not available right now.</p>}
      </section>

      <section className="rt-wrap rt-section">
        <h2 className="rt-title rt-title--sm">King Starline Charts</h2>
        <div className="rt-pills">
          {starline.slots.map((s) => (
            <Link key={s.slug} to={`/starline/${s.slug}`}>
              {s.name}
            </Link>
          ))}
        </div>
        <h2 className="rt-title rt-title--sm" style={{ marginTop: 34 }}>
          King Jackpot Charts
        </h2>
        <div className="rt-pills">
          {jackpot.slots.map((s) => (
            <Link key={s.slug} to={`/jackpot/${s.slug}`}>
              {s.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="rt-section rt-section--tint">
        <div className="rt-wrap rt-prose">
          <h2>Matka Jodi &amp; Panel Charts — Every Market in One Place</h2>
          <p>
            A <strong>Jodi chart</strong> shows the two-digit result of a market for every day, while a{" "}
            <strong>Pana (panel) chart</strong> adds the open and close pana around it. Both are grouped by week, with
            the newest week first, so you can read the latest result and the long-term pattern on one screen.
          </p>
          <p>
            Choose from Kalyan, Kalyan Morning and Night, Karnataka Day, Madhur, Main Bazar, Milan, Rajdhani, Ratan
            Khatri, Sridevi, Supreme and Time Bazar. Every chart page has a search box to find any jodi or pana quickly.
          </p>
        </div>
      </section>

      <section className="rt-wrap rt-cta-row">
        <DownloadButton />
      </section>
    </main>
  );
};

export default Charts;
